import type { TFuncKey } from 'i18next';
import { type ReactElement, useContext } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import DynamicT from '@/components/DynamicT';
import NavBar from '@/components/NavBar';
import PageMeta from '@/components/PageMeta';
import usePlatform from '@/hooks/use-platform';
import { getBrandingLogoUrl } from '@/utils/logo';

import { InlineNotification } from '../../components/Notification';

import styles from './index.module.scss';

type Props = {
  readonly title: TFuncKey;
  readonly description?: TFuncKey | ReactElement | '';
  readonly titleProps?: Record<string, unknown>;
  readonly descriptionProps?: Record<string, unknown>;
  readonly notification?: TFuncKey;
  readonly onSkip?: () => void;
  readonly isNavBarHidden?: boolean;
  readonly children: React.ReactNode;
};

const SecondaryPageLayout = ({
  title,
  description,
  titleProps,
  descriptionProps,
  notification,
  onSkip,
  isNavBarHidden,
  children,
}: Props) => {
  const { isMobile } = usePlatform();
  const { experienceSettings, theme } = useContext(PageContext);

  const logoUrl = experienceSettings
    ? getBrandingLogoUrl({
        theme,
        branding: experienceSettings.branding,
        isDarkModeEnabled: experienceSettings.color.isDarkModeEnabled,
      })
    : null;

  return (
    <div className={styles.wrapper}>
      <PageMeta titleKey={title} />
      <NavBar isHidden={isNavBarHidden} onSkip={onSkip} />
      {isMobile && notification && (
        <InlineNotification message={notification} className={styles.notification} />
      )}
      <div className={styles.container}>
        {logoUrl && (
          <div className={styles.logoWrapper}>
            <img className={styles.logo} alt="app logo" src={logoUrl} crossOrigin="anonymous" />
          </div>
        )}
        <div className={styles.header}>
          <div className={styles.title}>
            <DynamicT forKey={title} interpolation={titleProps} />
          </div>
          {description && (
            <div className={styles.description}>
              {typeof description === 'string' ? (
                <DynamicT forKey={description} interpolation={descriptionProps} />
              ) : (
                description
              )}
            </div>
          )}
        </div>
        {children}
      </div>
      {!isMobile && notification && (
        <InlineNotification message={notification} className={styles.notification} />
      )}
    </div>
  );
};

export default SecondaryPageLayout;
